import React, { memo, useCallback } from 'react';
import { Handle, Position, NodeProps } from '@xyflow/react';
import { NodeContent } from './NodeContent';
import { ResizeHandle } from './ResizeHandle';
import { useStore } from '../../store';

interface TextNodeData {
  text: string;
  timeEstimate: string;
  parentId: string | null;
  children: string[];
  // Style properties
  textColor?: string;
  fontFamily?: string;
  fontSize?: number;
  opacity?: number;
  textOverflow?: 'hidden' | 'visible';
}

export const TextNode = memo(({ id, data, selected }: NodeProps) => {
  const nodeData = data as unknown as TextNodeData;
  const nodes = useStore((state) => state.nodes);
  const addConnectedNode = useStore((state) => state.addConnectedNode);
  const node = nodes[id];

  // Apply custom width and styles
  const style: React.CSSProperties = {};
  if (node?.width) {
    style.width = `${node.width}px`;
  }
  if (node?.height) {
    style.minHeight = `${node.height}px`;
  }
  // Apply custom text styles from node data
  if (nodeData.textColor) {
    style.color = nodeData.textColor;
  }
  if (nodeData.fontFamily) {
    style.fontFamily = nodeData.fontFamily;
  }
  if (nodeData.fontSize) {
    style.fontSize = `${nodeData.fontSize}px`;
  }
  if (nodeData.opacity !== undefined) {
    style.opacity = nodeData.opacity;
  }

  const handleAddNode = useCallback(
    (direction: 'left' | 'right' | 'top' | 'bottom') => (e: React.MouseEvent) => {
      e.stopPropagation();
      addConnectedNode(id, direction, 'text');
    },
    [id, addConnectedNode]
  );

  const overflowClass = nodeData.textOverflow === 'visible' ? 'text-overflow-visible' : '';

  return (
    <div className={`node node-text ${selected ? 'selected' : ''} ${overflowClass}`} style={style}>
      {/* Handles kept for edge rendering but disabled for manual connections */}
      <Handle
        type="target"
        position={Position.Top}
        className="connection-handle connection-handle-top"
        isConnectable={false}
      />
      <Handle
        type="target"
        position={Position.Left}
        id="left"
        className="connection-handle connection-handle-left"
        isConnectable={false}
      />

      <NodeContent
        id={id}
        text={nodeData.text}
        timeEstimate={nodeData.timeEstimate}
        children={nodeData.children}
      />

      <Handle
        type="source"
        position={Position.Right}
        id="right"
        className="connection-handle connection-handle-right"
        isConnectable={false}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        className="connection-handle connection-handle-bottom"
        isConnectable={false}
      />

      {/* Add connected node buttons */}
      <button
        className="node-add-btn left"
        onClick={handleAddNode('left')}
        title="Add node to the left"
      >
        +
      </button>

      <button
        className="node-add-btn right"
        onClick={handleAddNode('right')}
        title="Add node to the right"
      >
        +
      </button>

      <button
        className="node-add-btn bottom"
        onClick={handleAddNode('bottom')}
        title="Add node below"
      >
        +
      </button>

      <ResizeHandle nodeId={id} />
    </div>
  );
});

TextNode.displayName = 'TextNode';
